import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { User, ChevronDown, LayoutDashboard, Home, LogOut, Heart, Receipt } from 'lucide-react'
import { Container } from './Container'
import { CitySelector } from './CitySelector'
import { NavMegaMenu } from './NavMegaMenu'
import { NavDropdown } from './NavDropdown'
import { Button } from '@/components/ui/button'
import { SITE } from '@/lib/site'
import { useAuthStore } from '@/store/authStore'
import { cn } from '@/lib/utils'
import { PATHS } from '@/routes/paths'
import { useUiStore } from '@/store/uiStore'
import { useGeolocation } from '@/hooks/useGeolocation'
import { useMegaMenu } from '@/hooks/useMegaMenu'
import { hasPropertySections } from '@/lib/megaMenu'
import { trackPostPropertyClick } from '@/lib/analytics/sellerEvents'
import { containsTarget } from '@/lib/dom'

const DIRECT_LINKS = [
  { label: 'QBC Premium', href: PATHS.premium },
  { label: 'Blog', href: PATHS.blog },
] as const

const ABOUT_LINKS = [
  { label: 'About Us', href: PATHS.about },
  { label: 'Magazine', href: PATHS.magazine },
  { label: 'Media Center', href: PATHS.media },
  { label: 'Achievements', href: PATHS.achievements },
  { label: 'Cost Estimator', href: PATHS.calculator },
  { label: 'Real Estate Information', href: PATHS.realEstateBook },
  { label: 'FAQs', href: PATHS.faq },
  { label: 'Contact Us', href: PATHS.contact },
] as const

export function Header() {
  const navigate = useNavigate()
  const [scrolled, setScrolled] = useState(false)
  const [userOpen, setUserOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement | null>(null)
  const { setAuthModalOpen } = useUiStore()
  const { isAuthenticated, user, logout } = useAuthStore()

  // First-visit location detection -> auto-select the nearest city (once).
  useGeolocation({ autoPrompt: true })

  // Mega menus built dynamically from live database results for the active city.
  const buyMenu = useMegaMenu('buy')
  const rentMenu = useMegaMenu('rent')

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (!userOpen) return

    const onPointerDown = (event: MouseEvent) => {
      if (containsTarget(userMenuRef.current, event.target)) return
      setUserOpen(false)
    }

    window.addEventListener('mousedown', onPointerDown)
    return () => window.removeEventListener('mousedown', onPointerDown)
  }, [userOpen])

  const handleLogout = async () => {
    await logout()
    setUserOpen(false)
    navigate(PATHS.home)
  }

  return (
    <header className="sticky top-0 z-50">
      <div
        className={cn(
          'border-b transition-all duration-200',
          scrolled ? 'glass-header border-neutral-200/80 shadow-sm' : 'border-neutral-100 bg-white',
        )}
      >
        <Container>
          <div className="flex h-14 items-center gap-2 md:h-[68px] md:gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-2 lg:gap-4">
              <Link to="/" className="flex shrink-0 items-center">
                <img
                  src={SITE.logoSmall}
                  alt={SITE.name}
                  width={195}
                  height={40}
                  style={{ aspectRatio: SITE.logoAspect }}
                  className="h-8 w-auto sm:h-9 lg:h-10"
                />
              </Link>
              <div className="hidden h-8 w-px bg-neutral-200 lg:block" aria-hidden />
              <div className="hidden lg:block">
                <CitySelector variant="header" />
              </div>
            </div>

            <nav className="hidden min-w-0 flex-1 flex-nowrap items-center justify-center gap-0 overflow-visible whitespace-nowrap lg:flex">
              {hasPropertySections(buyMenu) && <NavMegaMenu label="For Buyers" config={buyMenu} />}
              {hasPropertySections(rentMenu) && <NavMegaMenu label="For Tenants" config={rentMenu} />}
              {DIRECT_LINKS.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="shrink-0 cursor-pointer whitespace-nowrap px-2 py-2 text-[13px] font-semibold text-neutral-700 transition-colors hover:text-primary-600 xl:px-2.5 xl:text-sm"
                >
                  {link.label}
                </Link>
              ))}
              <NavDropdown label="About Us" links={ABOUT_LINKS} />
            </nav>

            <div className="ml-auto flex shrink-0 items-center gap-1 md:gap-1.5">
              <Button
                asChild
                className="h-8 gap-1 rounded-full bg-primary-500 px-2.5 text-[10px] shadow-primary sm:hidden"
              >
                <Link to={PATHS.addProperty} onClick={() => trackPostPropertyClick('header_mobile')}>
                  Post
                  <span className="rounded bg-success-500 px-1 py-px text-[8px] font-bold uppercase text-white">
                    Free
                  </span>
                </Link>
              </Button>

              <Button
                asChild
                className="hidden h-9 gap-1 rounded-full bg-primary-500 px-3 text-xs shadow-primary sm:flex xl:h-10 xl:px-4 xl:text-sm"
              >
                <Link to={PATHS.addProperty} onClick={() => trackPostPropertyClick('header')}>
                  Post Property
                  <span className="rounded bg-success-500 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
                    Free
                  </span>
                </Link>
              </Button>

              <div ref={userMenuRef} className="relative hidden md:block">
                {isAuthenticated ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setUserOpen(!userOpen)}
                      className="flex items-center gap-1.5 rounded-full border border-neutral-200 px-3 py-2 text-sm font-semibold text-neutral-700 hover:border-primary-200 hover:bg-primary-50"
                    >
                      <User className="h-4 w-4 text-primary-500" />
                      <span data-clarity-mask="True">{user?.name?.slice(0, 8) ?? 'Account'}</span>
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                    {userOpen && (
                      <div className="absolute right-0 top-full z-50 mt-2 w-52 rounded-xl border border-neutral-200 bg-white py-2 shadow-xl">
                        <Link to={PATHS.editProfile} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-primary-50" onClick={() => setUserOpen(false)}>
                          <User className="h-4 w-4" /> Profile
                        </Link>
                        <Link to={PATHS.dashboard} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-primary-50" onClick={() => setUserOpen(false)}>
                          <LayoutDashboard className="h-4 w-4" /> Dashboard
                        </Link>
                        <Link to={PATHS.myOrders} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-primary-50" onClick={() => setUserOpen(false)}>
                          <Receipt className="h-4 w-4" /> Orders & Payments
                        </Link>
                        <Link to={PATHS.myProperty} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-primary-50" onClick={() => setUserOpen(false)}>
                          <Home className="h-4 w-4" /> My Property
                        </Link>
                        <Link to={PATHS.myWishlist} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-primary-50" onClick={() => setUserOpen(false)}>
                          <Heart className="h-4 w-4" /> Wishlist Properties
                        </Link>
                        <button
                          type="button"
                          onClick={handleLogout}
                          className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-danger-500 hover:bg-danger-50"
                          aria-label="Logout"
                        >
                          <LogOut className="h-4 w-4" aria-hidden="true" /> Logout
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <Button variant="outline" size="sm" className="h-9 rounded-full px-3 text-xs xl:h-10 xl:text-sm" onClick={() => setAuthModalOpen(true)}>
                    <User className="h-4 w-4" /> Sign In
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Container>
      </div>
    </header>
  )
}
